import { isEqual, keys } from 'lodash-es';
import { useCallback, useMemo, useState, type SetStateAction } from 'react';

import {
  cleanupInitialFilterDateRange,
  FilterClauseType,
  filterExistsAndExcludes,
  removeEmptyFilters,
  useDateRangeFilter,
  type BlotterTableClientLocalFilter,
  type BlotterTableFilter,
  type BlotterTableFiltersProps,
  type DateRangeFilter,
  type FilterableProperty,
  type FilterClause,
  type Position,
  type UseFilterBuilderProps,
  type UsePersistedBlotterTable,
} from '@talos/kyoko';

import { useCustomerMarketAccountsFilter } from './filters/useCustomerMarketAccountsFilter';

export interface PositionsTableFilter extends DateRangeFilter {
  MarketAccounts?: string[];
  Assets?: string[];
}

interface UsePositionsFilterParams {
  persistedBlotterTable: UsePersistedBlotterTable;
}

export function usePositionsFilter<TData extends Position>({ persistedBlotterTable }: UsePositionsFilterParams) {
  const { onFilterChanged: saveFilter } = persistedBlotterTable;

  const [initialFilter] = useState<PositionsTableFilter>(() =>
    cleanupInitialFilterDateRange(persistedBlotterTable.initialState?.filter)
  );
  const [filter, setFilter] = useState<PositionsTableFilter>(() =>
    cleanupInitialFilterDateRange(persistedBlotterTable.initialState?.filter)
  );

  const changeFilter = useCallback(
    (action: SetStateAction<BlotterTableFilter>) => {
      const prevFilter = filter;
      const newFilter = action instanceof Function ? action(filter) : action;
      if (!isEqual(prevFilter, newFilter)) {
        setFilter(newFilter);
        saveFilter(newFilter);
      }
    },
    [filter, saveFilter]
  );

  const clientSideFilter = useCallback<BlotterTableClientLocalFilter<TData>>(
    row => {
      const data = row.data;
      if (filterExistsAndExcludes(filter, 'Assets', data, 'Asset')) {
        return false;
      }
      return true;
    },
    [filter]
  );

  const customerMarketAccountsFilter = useCustomerMarketAccountsFilter();

  const filterableProperties: FilterableProperty[] = useMemo(
    () => [customerMarketAccountsFilter].compact(),
    [customerMarketAccountsFilter]
  );

  const initialFilterClauses = useMemo(() => {
    const clauses: FilterClause[] = [];
    if (filter) {
      (keys(filter) as (keyof PositionsTableFilter)[]).forEach((key: keyof PositionsTableFilter) => {
        switch (key) {
          case 'StartDate':
          case 'EndDate':
            return;
          default:
            clauses.push({
              key: key,
              type: FilterClauseType.INCLUSIVE,
              selections: filter[key] as string[],
            });
        }
      });
    }
    return clauses;
  }, [filter]);
  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter: PositionsTableFilter = removeEmptyFilters<PositionsTableFilter>({
          ...curr,
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => [key, filterClausesByPropertyKey.get(key)?.selections])
          ) as any as PositionsTableFilter),
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter]
  );
  const dateRangeFilter = useDateRangeFilter(filter, changeFilter);

  const filterBuilderProps = useMemo(
    () => ({
      initialFilterClauses,
      properties: filterableProperties,
      onFilterClausesChanged: handleFilterClausesChanged,
    }),
    [filterableProperties, handleFilterClausesChanged, initialFilterClauses]
  ) satisfies UseFilterBuilderProps;
  const blotterTableFilterProps = useMemo(
    () => ({
      ...dateRangeFilter,
    }),
    [dateRangeFilter]
  ) satisfies Partial<BlotterTableFiltersProps>;
  return useMemo(
    () => ({
      initialFilter,
      clientSideFilter,
      filter,
      // shortcut to spread properties into useAccordionFilterBuilder.filterBuilderProps
      filterBuilderProps,
      // shortcut to spread props into the BlotterTableFilters component
      blotterTableFilterProps,
    }),
    [blotterTableFilterProps, clientSideFilter, filter, filterBuilderProps, initialFilter]
  );
}
